import React, { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';

/**
 * Detect supported barcode format from raw value.
 * - EAN13: 13 numeric digits
 * - UPC (UPC-A): 12 numeric digits
 * - CODE128: fallback for any alphanumeric
 * Returns null if value is empty/invalid for rendering.
 */
const detectFormat = (raw) => {
  if (!raw) return null;
  const v = String(raw).trim();
  if (!v) return null;
  // Reject placeholder zeros like "0000000000000"
  if (/^0+$/.test(v)) return null;
  if (/^\d{13}$/.test(v)) return 'EAN13';
  if (/^\d{12}$/.test(v)) return 'UPC';
  if (/^\d{8}$/.test(v)) return 'EAN8';
  // For any other non-empty text, fall back to CODE128 (printable scannable)
  if (/^[\x20-\x7E]+$/.test(v) && v.length >= 4) return 'CODE128';
  return null;
};

/**
 * Renders a scannable barcode using JsBarcode.
 * Props:
 *  - value: string code
 *  - height: barcode bar height (px), default 50
 *  - fontSize: text under bars
 *  - displayValue: show numeric value below bars
 *  - className: wrapper className
 */
const Barcode = ({ value, height = 50, fontSize = 12, displayValue = true, className = '' }) => {
  const ref = useRef(null);
  const format = detectFormat(value);

  useEffect(() => {
    if (!ref.current || !format) return;
    try {
      JsBarcode(ref.current, String(value).trim(), {
        format,
        height,
        fontSize,
        displayValue,
        margin: 0,
        marginTop: 4,
        marginBottom: 4,
        background: '#ffffff',
        lineColor: '#0f172a',
        textMargin: 2,
      });
    } catch (err) {
      // Invalid barcode (e.g. checksum mismatch). Silently hide.
      // eslint-disable-next-line no-console
      console.warn('[Barcode] failed to render', value, err?.message);
      if (ref.current) {
        while (ref.current.firstChild) ref.current.removeChild(ref.current.firstChild);
      }
    }
  }, [value, format, height, fontSize, displayValue]);

  if (!format) return null;

  return (
    <div className={`flex items-center justify-center overflow-hidden rounded-md bg-white ${className}`}>
      <svg ref={ref} aria-label={`Código de barras ${value}`} />
    </div>
  );
};

export default Barcode;
